<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { musicTrackUrl, parseMusicCatalog, type MusicTrack } from '@/domain/music/catalog'
import { classifyPlaybackFailure, createPlaybackRequestGuard } from '@/domain/music/playback'

type PlaybackMode = 'playlist' | 'single'
type PlayerState = 'loading' | 'ready' | 'playing' | 'paused' | 'error'

const STORAGE_PREFIX = 'nixing-music-player:'
const route = useRoute()
const root = ref<HTMLElement | null>(null)
const panel = ref<HTMLElement | null>(null)
const audio = ref<HTMLAudioElement | null>(null)
const tracks = ref<MusicTrack[]>([])
const expanded = ref(false)
const selectedTrackId = ref(readStoredString('track'))
const playbackMode = ref<PlaybackMode>(
  readStoredString('mode') === 'single' ? 'single' : 'playlist',
)
const volume = ref(readStoredVolume())
const state = ref<PlayerState>('loading')
const message = ref('')
const autoplayPending = ref(true)
const playbackRequests = createPlaybackRequestGuard()

const currentTrack = computed(
  () => tracks.value.find((track) => track.id === selectedTrackId.value) ?? tracks.value[0],
)
const isStudioRoute = computed(() => route.name === 'studio')
const currentIndex = computed(() =>
  currentTrack.value ? tracks.value.findIndex((track) => track.id === currentTrack.value?.id) : -1,
)
const triggerLabel = computed(() => {
  if (state.value === 'playing' && currentTrack.value)
    return `音乐播放中：${currentTrack.value.displayName}`
  if (tracks.value.length === 0 && state.value !== 'loading') return '打开音乐播放器，当前没有音乐'
  return expanded.value ? '收起音乐播放器' : '打开音乐播放器'
})

function readStoredString(key: string): string {
  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${key}`) ?? ''
  } catch {
    return ''
  }
}

function readStoredVolume(): number {
  const storedValue = readStoredString('volume')
  if (storedValue === '') return 0.5
  const value = Number(storedValue)
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : 0.5
}

function persist(key: string, value: string): void {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, value)
  } catch {
    // Playback remains available when storage is blocked or full.
  }
}

function resolveTrackUrl(track: MusicTrack): string {
  return musicTrackUrl(track, window.location.origin, import.meta.env.BASE_URL)
}

function applyAudioSettings(): void {
  if (!audio.value) return
  audio.value.loop = playbackMode.value === 'single'
  try {
    audio.value.volume = volume.value
  } catch {
    message.value = '当前浏览器请使用系统音量键调节音量。'
  }
}

function loadCurrentTrack(shouldPlay: boolean, automatic = false): void {
  const element = audio.value
  const track = currentTrack.value
  if (!element || !track) return
  playbackRequests.invalidate()
  element.pause()
  element.src = resolveTrackUrl(track)
  applyAudioSettings()
  element.load()
  state.value = 'ready'
  message.value = ''
  if (shouldPlay) void playCurrent(automatic)
}

async function playCurrent(automatic = false): Promise<void> {
  const element = audio.value
  const track = currentTrack.value
  if (!element || !track) return
  if (!element.src) loadCurrentTrack(false)
  const requestId = playbackRequests.begin()
  const trackId = track.id
  try {
    state.value = 'loading'
    await element.play()
    if (!playbackRequests.isCurrent(requestId) || currentTrack.value?.id !== trackId) return
    autoplayPending.value = false
    state.value = element.paused ? 'paused' : 'playing'
    message.value = ''
  } catch (error) {
    if (!playbackRequests.isCurrent(requestId) || currentTrack.value?.id !== trackId) return
    const failure = classifyPlaybackFailure(error)
    if (failure === 'aborted') {
      state.value = element.paused ? 'paused' : 'playing'
      return
    }
    if (failure === 'blocked') {
      state.value = 'paused'
      autoplayPending.value = automatic
      message.value = automatic
        ? '浏览器暂未允许有声自动播放，首次操作页面后将自动开始。'
        : '浏览器阻止了播放，请再次点击播放。'
      return
    }
    autoplayPending.value = false
    state.value = 'error'
    message.value =
      failure === 'unsupported'
        ? '浏览器无法解码这首音乐，请选择其他音乐。'
        : '这首音乐暂时无法播放，请稍后重试。'
    console.warn('[music-player] playback failed', error)
  }
}

function togglePlayback(): void {
  if (!currentTrack.value || !audio.value) return
  autoplayPending.value = false
  if (audio.value.paused) {
    void playCurrent()
  } else {
    playbackRequests.invalidate()
    audio.value.pause()
    message.value = ''
  }
}

function selectTrack(id: string, shouldPlay: boolean): void {
  if (!tracks.value.some((track) => track.id === id)) return
  selectedTrackId.value = id
  persist('track', id)
  loadCurrentTrack(shouldPlay)
}

function selectTrackFromEvent(event: Event): void {
  const shouldPlay =
    state.value === 'playing' || state.value === 'loading' || audio.value?.paused === false
  selectTrack((event.target as HTMLSelectElement).value, shouldPlay)
}

function moveTrack(offset: number, shouldPlay = audio.value?.paused === false): void {
  if (tracks.value.length === 0) return
  const index = currentIndex.value < 0 ? 0 : currentIndex.value
  const nextIndex = (index + offset + tracks.value.length) % tracks.value.length
  const next = tracks.value[nextIndex]
  if (next) selectTrack(next.id, shouldPlay)
}

function handleEnded(): void {
  if (playbackMode.value === 'single') return
  moveTrack(1, true)
}

function handleAudioError(): void {
  const element = audio.value
  const track = currentTrack.value
  if (!element || !track) return
  const activeUrl = element.currentSrc || element.src
  if (activeUrl && activeUrl !== resolveTrackUrl(track)) return
  playbackRequests.invalidate()
  autoplayPending.value = false
  state.value = 'error'
  message.value = `“${track.displayName}”无法解码或加载，请选择其他音乐。`
}

function handlePlaying(): void {
  autoplayPending.value = false
  state.value = 'playing'
  message.value = ''
}

function handlePause(): void {
  if (state.value === 'playing') state.value = 'paused'
}

function handleVolumeInput(event: Event): void {
  volume.value = Number((event.target as HTMLInputElement).value)
  applyAudioSettings()
  persist('volume', String(volume.value))
}

function handleModeChange(): void {
  applyAudioSettings()
  persist('mode', playbackMode.value)
}

async function toggleExpanded(): Promise<void> {
  expanded.value = !expanded.value
  if (!expanded.value) return
  await nextTick()
  panel.value?.querySelector<HTMLElement>('button, select, input')?.focus()
}

function handleDocumentPointerDown(event: PointerEvent): void {
  if (expanded.value && !root.value?.contains(event.target as Node)) expanded.value = false
  if ((event.target as Element | null)?.closest?.('.music-player-transport')) return
  resumeAutoplayFromGesture()
}

function handleDocumentKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && expanded.value) expanded.value = false
  if ((event.target as Element | null)?.closest?.('.music-player-transport')) return
  resumeAutoplayFromGesture()
}

function resumeAutoplayFromGesture(): void {
  if (!autoplayPending.value || !currentTrack.value || !audio.value?.paused) return
  autoplayPending.value = false
  void playCurrent()
}

async function loadCatalog(): Promise<void> {
  state.value = 'loading'
  try {
    const base = new URL(import.meta.env.BASE_URL, window.location.origin)
    const response = await fetch(new URL('assets/manifest.json', base), { cache: 'no-store' })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    tracks.value = parseMusicCatalog(await response.json())
    if (tracks.value.length === 0) {
      autoplayPending.value = false
      selectedTrackId.value = ''
      state.value = 'ready'
      message.value = '尚未发现 MP3，请将音乐复制到 public/assets/music。'
      return
    }
    if (!tracks.value.some((track) => track.id === selectedTrackId.value)) {
      selectedTrackId.value = tracks.value[0]!.id
    }
    await nextTick()
    loadCurrentTrack(true, true)
  } catch {
    autoplayPending.value = false
    tracks.value = []
    state.value = 'error'
    message.value = '音乐列表加载失败，其他创作功能不受影响。'
  }
}

watch(playbackMode, handleModeChange)

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown)
  document.addEventListener('keydown', handleDocumentKeydown)
  void loadCatalog()
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown)
  document.removeEventListener('keydown', handleDocumentKeydown)
  playbackRequests.invalidate()
  audio.value?.pause()
})
</script>

<template>
  <div
    ref="root"
    class="music-player"
    :class="{ expanded, playing: state === 'playing', 'studio-context': isStudioRoute }"
  >
    <button
      class="music-player-trigger"
      type="button"
      :aria-label="triggerLabel"
      :aria-expanded="expanded"
      aria-controls="music-player-panel"
      :title="triggerLabel"
      @click="toggleExpanded"
    >
      <span aria-hidden="true">♫</span>
      <i v-if="state === 'playing'" aria-hidden="true"></i>
    </button>

    <section
      v-if="expanded"
      id="music-player-panel"
      ref="panel"
      class="music-player-panel"
      role="region"
      aria-label="音乐播放器"
    >
      <header>
        <div>
          <small>工坊音乐</small>
          <strong>{{ currentTrack?.displayName ?? '尚无音乐' }}</strong>
        </div>
        <button type="button" aria-label="收起音乐播放器" @click="expanded = false">×</button>
      </header>

      <template v-if="tracks.length">
        <label>
          <span>选择音乐</span>
          <select :value="currentTrack?.id" @change="selectTrackFromEvent">
            <option v-for="track in tracks" :key="track.id" :value="track.id">
              {{ track.displayName }}
            </option>
          </select>
        </label>

        <div class="music-player-transport" aria-label="播放控制">
          <button type="button" aria-label="上一首" @click="moveTrack(-1)">‹</button>
          <button
            type="button"
            :aria-label="state === 'playing' ? '暂停' : '播放'"
            @click="togglePlayback"
          >
            {{ state === 'playing' ? 'Ⅱ' : '▶' }}
          </button>
          <button type="button" aria-label="下一首" @click="moveTrack(1)">›</button>
        </div>

        <label>
          <span>播放模式</span>
          <select v-model="playbackMode">
            <option value="playlist">列表循环</option>
            <option value="single">单曲循环</option>
          </select>
        </label>

        <label>
          <span
            >音量 <output>{{ Math.round(volume * 100) }}%</output></span
          >
          <input
            :value="volume"
            type="range"
            min="0"
            max="1"
            step="0.01"
            aria-label="音乐音量"
            @input="handleVolumeInput"
          />
        </label>
        <p class="music-player-note">部分 iPhone/iPad 浏览器需要使用设备音量键。</p>
      </template>

      <p
        v-if="message"
        class="music-player-message"
        :class="{ error: state === 'error' }"
        role="status"
      >
        {{ message }}
      </p>
    </section>

    <audio
      ref="audio"
      preload="auto"
      @play="handlePlaying"
      @playing="handlePlaying"
      @pause="handlePause"
      @ended="handleEnded"
      @error="handleAudioError"
    ></audio>
  </div>
</template>
